import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bold,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Download,
  Inbox,
  Italic,
  Loader2,
  Mail,
  MoreHorizontal,
  Paperclip,
  PauseCircle,
  RefreshCw,
  Reply,
  Search,
  Send,
  Underline,
  X,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import api from "../api";
import { Avatar, Pill, Spinner } from "../components/ui-bits";
import { useToast } from "../hooks/use-toast";
import "./MailboxPage.css";

const scopes = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "sequence_replies", label: "Sequence replies" },
];

function initials(name, fallback = "?") {
  const value = (name || "").trim();
  if (!value) return fallback;
  return value.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
}

function formatDate(value, withYear = false) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      ...(withYear ? { year: "numeric" } : {}),
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function formatListTime(value) {
  if (!value) return "";
  try {
    const date = new Date(value);
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
      return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(date);
    }
    return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
  } catch {
    return value;
  }
}

function messageAuthor(message, thread) {
  if (message.direction === "outbound") return "You";
  return message.from_name || thread.person_name || message.from_email || "Customer";
}

function messageInitials(message, thread) {
  return message.direction === "outbound" ? "AR" : initials(thread.person_name, initials(message.from_email));
}

function formatBytes(size) {
  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function makeIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function ThreadRow({ thread, selected, onSelect }) {
  const person = thread.person_name || thread.person_email || "Unknown sender";
  return (
    <button
      type="button"
      className={`mailbox-thread-row ${selected ? "selected" : ""} ${thread.unread ? "unread" : ""}`}
      onClick={() => onSelect(thread.id)}
      aria-current={selected ? "true" : undefined}
    >
      <Avatar text={initials(person)} brand size={30} />
      <span className="mailbox-thread-row-main">
        <span className="mailbox-thread-row-top">
          <span className="mailbox-thread-row-name">{person}</span>
          <time>{formatListTime(thread.last_message_at)}</time>
        </span>
        <span className="mailbox-thread-row-subject">{thread.subject || "(no subject)"}</span>
        <span className="mailbox-thread-row-preview">{thread.last_message_preview || "No message preview"}</span>
        <span className="mailbox-thread-row-tags">
          {thread.sequence_name && <Pill className="bg-[hsl(245,58%,96%)] text-[hsl(245,50%,48%)]">{thread.sequence_name}</Pill>}
          {thread.message_count > 1 && <span className="mailbox-message-count">{thread.message_count} messages</span>}
        </span>
      </span>
    </button>
  );
}

function MessageCard({ message, thread }) {
  const outbound = message.direction === "outbound";
  const body = message.body_text || "(No text body)";
  return (
    <article className={`mailbox-message ${outbound ? "outbound" : "inbound"}`}>
      <Avatar text={messageInitials(message, thread)} brand={outbound} size={28} />
      <div className="mailbox-message-content">
        <div className="mailbox-message-meta">
          <span className="mailbox-message-author">{messageAuthor(message, thread)}</span>
          <time>{formatDate(message.created_at || message.received_at)}</time>
          {message.automated && <span className="mailbox-automated-label">Automatic reply</span>}
        </div>
        <div className="mailbox-message-card">
          {body}
          <MessageAttachments message={message} />
        </div>
      </div>
    </article>
  );
}

function MessageAttachments({ message }) {
  const sourceAttachments = message.attachments;
  const [attachments, setAttachments] = useState(sourceAttachments || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setAttachments(sourceAttachments || []); }, [message.id, sourceAttachments]);

  const loadLinks = async () => {
    setLoading(true);
    try {
      const fresh = await api.mailboxMessageAttachments(message.id);
      setAttachments(fresh || []);
    } catch {
      // The stored metadata remains useful even when the signed URL request fails.
    } finally {
      setLoading(false);
    }
  };

  if (!attachments.length) return null;
  return (
    <div className="mailbox-attachments" aria-label="Attachments">
      {attachments.map((attachment, index) => (
        attachment.download_url ? (
          <a key={attachment.id || `${attachment.filename}-${index}`} href={attachment.download_url} target="_blank" rel="noreferrer" className="mailbox-attachment-chip">
            <Download className="h-3 w-3" /> {attachment.filename || "Attachment"} {attachment.size ? <span>{formatBytes(attachment.size)}</span> : null}
          </a>
        ) : (
          <span key={attachment.id || `${attachment.filename}-${index}`} className="mailbox-attachment-chip">
            <Paperclip className="h-3 w-3" /> {attachment.filename || "Attachment"} {attachment.size ? <span>{formatBytes(attachment.size)}</span> : null}
            {message.direction === "inbound" && <button type="button" onClick={loadLinks} disabled={loading} className="mailbox-attachment-action">{loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Get link"}</button>}
          </span>
        )
      ))}
    </div>
  );
}

function RichComposer({ html, setHtml, setText, disabled, placeholder }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (html || "")) {
      editorRef.current.innerHTML = html || "";
    }
  }, [html]);

  const sync = (event) => {
    const editor = event.currentTarget;
    setHtml(editor.innerHTML);
    setText((editor.innerText || editor.textContent || "").replace(/\u00a0/g, " "));
  };

  const command = (name) => {
    editorRef.current?.focus();
    document.execCommand(name, false);
    if (editorRef.current) sync({ currentTarget: editorRef.current });
  };

  return (
    <>
      <div
        ref={editorRef}
        id="mailbox-editor"
        className="mailbox-rich-editor"
        contentEditable={!disabled}
        data-placeholder={placeholder}
        data-testid="mailbox-editor"
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder}
        onInput={sync}
        suppressContentEditableWarning
      />
      <div className="mailbox-composer-toolbar" role="toolbar" aria-label="Formatting">
        <button type="button" aria-label="Bold" title="Bold" onMouseDown={(event) => event.preventDefault()} onClick={() => command("bold")} disabled={disabled}><Bold className="h-3.5 w-3.5" /></button>
        <button type="button" aria-label="Italic" title="Italic" onMouseDown={(event) => event.preventDefault()} onClick={() => command("italic")} disabled={disabled}><Italic className="h-3.5 w-3.5" /></button>
        <button type="button" aria-label="Underline" title="Underline" onMouseDown={(event) => event.preventDefault()} onClick={() => command("underline")} disabled={disabled}><Underline className="h-3.5 w-3.5" /></button>
      </div>
    </>
  );
}

function SequenceContext({ thread, onViewPerson }) {
  if (!thread) return null;
  const stopped = thread.sequence_stopped;
  return (
    <aside className="mailbox-context">
      <section className="mailbox-context-section">
        <div className="mailbox-context-kicker">Sequence</div>
        {thread.sequence_name ? (
          <div className="mailbox-sequence-card">
            <div className="mailbox-sequence-title"><span className="mailbox-sequence-icon"><Reply className="h-3.5 w-3.5" /></span>{thread.sequence_name}</div>
            <div className={`mailbox-sequence-state ${stopped ? "stopped" : "active"}`}>
              {stopped ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock3 className="h-3.5 w-3.5" />}
              <span>{stopped ? "Stopped on reply" : "Sequence active"}</span>
            </div>
            <p>{stopped ? "Future steps will not be sent." : "A customer reply will stop future steps."}</p>
          </div>
        ) : (
          <p className="mailbox-context-empty">This conversation is not linked to a sequence.</p>
        )}
      </section>

      <section className="mailbox-context-section">
        <div className="mailbox-context-kicker">Person</div>
        <div className="mailbox-context-person"><Avatar text={initials(thread.person_name)} brand size={32} /><div><div className="font-medium">{thread.person_name || "Unknown sender"}</div><div className="text-[11px] text-[hsl(240,4%,50%)]">{thread.person_email}</div></div></div>
        <dl className="mailbox-detail-list">
          <div><dt>Last activity</dt><dd>{formatDate(thread.last_message_at)}</dd></div>
          <div><dt>Messages</dt><dd>{thread.message_count || 0}</dd></div>
          <div><dt>Source</dt><dd>{thread.linked ? "Sequence reply" : "Inbound email"}</dd></div>
        </dl>
      </section>

      <section className="mailbox-context-section mailbox-context-actions">
        <div className="mailbox-context-kicker">Thread</div>
        {thread.person_id ? (
          <button type="button" className="mailbox-context-link" onClick={onViewPerson}>View person record <ChevronRight className="h-3.5 w-3.5" /></button>
        ) : (
          <p className="mailbox-context-empty">No CRM person record linked.</p>
        )}
      </section>
    </aside>
  );
}

function ComposerForm({ mode = "reply", targetLabel, subject, setSubject, body, setBody, bodyHtml, setBodyHtml, attachments, onRemoveAttachment, onChooseFiles, fileInputRef, sending, signature, includeSignature, setIncludeSignature, onSubmit }) {
  const isNewEmail = mode === "new";
  const target = targetLabel || "customer";
  return (
    <form className="mailbox-composer" onSubmit={onSubmit}>
      {isNewEmail && <div className="mb-2"><label htmlFor="mailbox-subject" className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[hsl(240,4%,52%)]">Subject</label><input id="mailbox-subject" aria-label="Subject" value={subject} onChange={(event) => setSubject(event.target.value)} className="w-full rounded-md border border-[hsl(240,6%,88%)] bg-white px-2.5 py-2 text-[13px] outline-none focus:ring-2 focus:ring-[hsl(245,58%,70%)]" placeholder="Subject" disabled={sending} /></div>}
      <label className="sr-only" htmlFor="mailbox-editor">{isNewEmail ? `Email ${target}` : `Reply to ${target}`}</label>
      <RichComposer html={bodyHtml} setHtml={setBodyHtml} setText={setBody} placeholder={isNewEmail ? `Write to ${target}…` : `Reply to ${target}…`} disabled={sending} />
      {attachments.length > 0 && <div className="mailbox-composer-attachments">{attachments.map((attachment, index) => <span key={`${attachment.filename}-${index}`} className="mailbox-attachment-chip"><Paperclip className="h-3 w-3" /> {attachment.filename} <span>{formatBytes(attachment.size)}</span><button type="button" aria-label={`Remove ${attachment.filename}`} onClick={() => onRemoveAttachment(index)}><X className="h-3 w-3" /></button></span>)}</div>}
      <div className="mailbox-composer-footer">
        <div className="mailbox-composer-options">
          <input ref={fileInputRef} id="mailbox-attachments" type="file" multiple className="sr-only" onChange={onChooseFiles} disabled={sending} />
          <button type="button" className="mailbox-composer-tool" aria-label="Attach files" onClick={() => fileInputRef.current?.click()} disabled={sending}><Paperclip className="h-3.5 w-3.5" /> Attach</button>
          {signature && <label className="mailbox-signature-option"><input type="checkbox" checked={includeSignature} onChange={(event) => setIncludeSignature(event.target.checked)} disabled={sending} /> Add signature</label>}
          {!signature && <span className="mailbox-composer-hint">Add a signature in Integrations when needed.</span>}
        </div>
        <button type="submit" className="mailbox-send-button" disabled={sending || !body.trim() || (isNewEmail && !subject.trim())}>{sending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {sending ? "Sending" : isNewEmail ? "Send email" : "Send reply"}</button>
      </div>
    </form>
  );
}

function ThreadView({ thread, reply, replyHtml, setReplyHtml, setReply, attachments, onRemoveAttachment, onChooseFiles, fileInputRef, sending, signature, includeSignature, setIncludeSignature, onSubmit }) {
  if (!thread) {
    return <div className="mailbox-empty-thread"><Inbox className="h-6 w-6" /><div className="font-medium">Select a conversation</div><p>Customer replies and your responses will appear here.</p></div>;
  }
  const stopped = thread.sequence_stopped;
  return (
    <section className="mailbox-thread-pane">
      <header className="mailbox-thread-header">
        <div className="mailbox-contact-heading"><Avatar text={initials(thread.person_name)} brand size={34} /><div className="min-w-0"><div className="mailbox-contact-name">{thread.person_name || "Unknown sender"}</div><div className="mailbox-contact-email">{thread.person_email}</div></div></div>
        <div className="mailbox-thread-actions">
          <span className={`mailbox-status-pill ${stopped ? "stopped" : "active"}`}>{stopped ? <><Check className="h-3 w-3" /> Stopped on reply</> : <><Clock3 className="h-3 w-3" /> Awaiting reply</>}</span>
          <button type="button" className="mailbox-icon-button" aria-label="More conversation actions"><MoreHorizontal className="h-4 w-4" /></button>
        </div>
      </header>

      <div className="mailbox-thread-scroll">
        <div className="mailbox-thread-subject"><span>{thread.subject || "(no subject)"}</span>{thread.sequence_name && <Pill className="bg-[hsl(245,58%,96%)] text-[hsl(245,50%,48%)]">{thread.sequence_name}</Pill>}</div>
        <div className="mailbox-timeline">
          {(thread.messages || []).map((message) => <MessageCard key={message.id} message={message} thread={thread} />)}
        </div>
        {stopped && <div className="mailbox-stop-banner"><PauseCircle className="h-4 w-4" /><span><strong>Sequence paused after reply.</strong> Future steps for this contact are stopped automatically.</span></div>}
      </div>

      <ComposerForm targetLabel={thread.person_name} body={reply} setBody={setReply} bodyHtml={replyHtml} setBodyHtml={setReplyHtml} attachments={attachments} onRemoveAttachment={onRemoveAttachment} onChooseFiles={onChooseFiles} fileInputRef={fileInputRef} sending={sending} signature={signature} includeSignature={includeSignature} setIncludeSignature={setIncludeSignature} onSubmit={onSubmit} />
    </section>
  );
}

function FreshEmailView({ person, subject, setSubject, reply, replyHtml, setReplyHtml, setReply, attachments, onRemoveAttachment, onChooseFiles, fileInputRef, sending, signature, includeSignature, setIncludeSignature, onSubmit }) {
  return (
    <section className="mailbox-thread-pane">
      <header className="mailbox-thread-header">
        <div className="mailbox-contact-heading"><Avatar text={initials(person.name)} brand size={34} /><div className="min-w-0"><div className="mailbox-contact-name">New email</div><div className="mailbox-contact-email">To {person.name} · {person.email}</div></div></div>
        <div className="mailbox-thread-actions"><span className="mailbox-status-pill active"><Mail className="h-3 w-3" /> New email</span></div>
      </header>
      <div className="mailbox-thread-scroll">
        <div className="mailbox-thread-subject"><span>New message to {person.name}</span></div>
        <div className="px-4 py-4 text-[13px] text-[hsl(240,4%,48%)]">This email will start a new Mailbox conversation.</div>
      </div>
      <ComposerForm mode="new" targetLabel={person.name} subject={subject} setSubject={setSubject} body={reply} setBody={setReply} bodyHtml={replyHtml} setBodyHtml={setReplyHtml} attachments={attachments} onRemoveAttachment={onRemoveAttachment} onChooseFiles={onChooseFiles} fileInputRef={fileInputRef} sending={sending} signature={signature} includeSignature={includeSignature} setIncludeSignature={setIncludeSignature} onSubmit={onSubmit} />
    </section>
  );
}

export default function MailboxPage() {
  const { toast } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const [searchParams, setSearchParams] = useSearchParams();
  const composePersonId = searchParams.get("compose");
  const [scope, setScope] = useState("all");
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [composePerson, setComposePerson] = useState(null);
  const [composeLoading, setComposeLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [subject, setSubject] = useState("");
  const [reply, setReply] = useState("");
  const [replyHtml, setReplyHtml] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [signature, setSignature] = useState("");
  const [includeSignature, setIncludeSignature] = useState(false);
  const attachmentInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const nextThreads = await api.mailboxThreads(scope);
      setThreads(nextThreads || []);
      setSelectedId((current) => composePersonId ? null : (nextThreads || []).some((item) => item.id === current) ? current : nextThreads?.[0]?.id || null);
      return nextThreads || [];
    } catch (error) {
      toastRef.current({ title: "Unable to load Mailbox", description: error?.response?.data?.detail || "Check your connection and try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [scope, composePersonId]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  useEffect(() => {
    api.integrationSettings().then((settings) => {
      const value = settings?.email_signature || "";
      setSignature(value);
      setIncludeSignature(Boolean(value));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!composePersonId) {
      setComposePerson(null);
      setComposeLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSelectedId(null);
    setThread(null);
    setComposeLoading(true);
    api.person(composePersonId)
      .then((person) => { if (!cancelled) setComposePerson(person); })
      .catch((error) => {
        if (!cancelled) toastRef.current({ title: "Unable to load recipient", description: error?.response?.data?.detail || "Check the person record and try again.", variant: "destructive" });
      })
      .finally(() => { if (!cancelled) setComposeLoading(false); });
    return () => { cancelled = true; };
  }, [composePersonId]);

  useEffect(() => {
    setReply("");
    setReplyHtml("");
    setAttachments([]);
    setSubject("");
  }, [selectedId, composePersonId]);

  useEffect(() => {
    if (!selectedId) { setThread(null); return undefined; }
    let cancelled = false;
    setDetailLoading(true);
    api.mailboxThread(selectedId)
      .then(async (nextThread) => {
        if (cancelled) return;
        setThread(nextThread);
        if (nextThread.unread) {
          await api.markMailboxThreadRead(selectedId);
          if (!cancelled) {
            setThread((current) => current ? { ...current, unread: false } : current);
            setThreads((current) => current.map((item) => item.id === selectedId ? { ...item, unread: false } : item));
          }
        }
      })
      .catch((error) => {
        if (!cancelled) toastRef.current({ title: "Unable to open conversation", description: error?.response?.data?.detail || "Check your connection and try again.", variant: "destructive" });
      })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId]);

  const visibleThreads = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return threads;
    return threads.filter((item) => [item.person_name, item.person_email, item.subject, item.last_message_preview, item.sequence_name].join(" ").toLowerCase().includes(normalized));
  }, [query, threads]);

  const refresh = async () => {
    setRefreshing(true);
    await loadThreads();
    if (selectedId) {
      try { setThread(await api.mailboxThread(selectedId)); } catch { /* the list error already provides feedback */ }
    }
    setRefreshing(false);
  };

  const submitReply = async (event) => {
    event.preventDefault();
    const text = reply.trim();
    if (!text || !thread) return;
    setSending(true);
    try {
      const updated = await api.replyMailbox(thread.id, text, makeIdempotencyKey(), {
        html: replyHtml,
        attachments,
        include_signature: includeSignature,
      });
      setThread(updated);
      setReply("");
      setReplyHtml("");
      setAttachments([]);
      await loadThreads();
      toastRef.current({ title: "Reply sent", description: `Sent to ${thread.person_name || thread.person_email}` });
    } catch (error) {
      toastRef.current({ title: "Unable to send reply", description: error?.response?.data?.detail || "Check your connection and try again.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const submitNewEmail = async (event) => {
    event.preventDefault();
    const text = reply.trim();
    const nextSubject = subject.trim();
    if (!text || !nextSubject || !composePerson?.email) return;
    setSending(true);
    try {
      const created = await api.composeMailbox(composePerson.id, nextSubject, text, makeIdempotencyKey(), {
        html: replyHtml,
        attachments,
        include_signature: includeSignature,
      });
      setReply("");
      setReplyHtml("");
      setSubject("");
      setAttachments([]);
      await loadThreads();
      setSelectedId(created.id);
      setThread(created);
      setSearchParams({});
      toastRef.current({ title: "Email sent", description: `Sent to ${composePerson.name || composePerson.email}` });
    } catch (error) {
      toastRef.current({ title: "Unable to send email", description: error?.response?.data?.detail || "Check your connection and try again.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const chooseFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    const available = attachments.reduce((total, item) => total + item.size, 0);
    const next = [];
    let total = available;
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024 || total + file.size > 40 * 1024 * 1024) {
        toastRef.current({ title: "Attachment too large", description: "Each file must be under 10 MB and the total must be under 40 MB.", variant: "destructive" });
        continue;
      }
      const content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      next.push({ filename: file.name, content, content_type: file.type || "application/octet-stream", size: file.size });
      total += file.size;
    }
    if (next.length) setAttachments((current) => [...current, ...next]);
    event.target.value = "";
  };

  return (
    <div className="h-full flex flex-col animate-in-up">
      <div className="mailbox-page-header">
        <div className="mailbox-page-title"><div className="mailbox-page-icon"><Mail className="h-4 w-4" /></div><div><h1>Mailbox</h1><p>Customer replies from your outreach, in one place.</p></div></div>
        <div className="mailbox-page-actions"><span className="mailbox-sync-status"><span />Synced just now</span><button type="button" className="mailbox-header-button" onClick={refresh} disabled={refreshing}><RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh</button></div>
      </div>

      <div className="mailbox-layout">
        <aside className="mailbox-list-pane">
          <div className="mailbox-list-toolbar"><div className="mailbox-search"><Search className="h-3.5 w-3.5" /><input aria-label="Search conversations" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search conversations" /></div><div className="mailbox-scope-tabs">{scopes.map((item) => <button type="button" key={item.value} className={scope === item.value ? "active" : ""} onClick={() => setScope(item.value)}>{item.label}{item.value === "unread" && <span> · {threads.filter((threadItem) => threadItem.unread).length}</span>}</button>)}</div></div>
          <div className="mailbox-list-heading"><span>{scopes.find((item) => item.value === scope)?.label}</span><span>{visibleThreads.length} {visibleThreads.length === 1 ? "thread" : "threads"}</span></div>
          <div className="mailbox-thread-list">
            {loading ? <Spinner label="Loading conversations…" /> : visibleThreads.length === 0 ? <div className="mailbox-list-empty"><Inbox className="h-5 w-5" /><div>{query ? "No conversations match this search." : "No customer replies yet."}</div><p>{query ? "Try a different name, email, or subject." : "Replies will appear here when customers respond."}</p></div> : visibleThreads.map((item) => <ThreadRow key={item.id} thread={item} selected={item.id === selectedId} onSelect={setSelectedId} />)}
          </div>
        </aside>

        {composePersonId ? (composeLoading ? <div className="mailbox-detail-loading"><Spinner label="Loading recipient…" /></div> : composePerson ? <FreshEmailView person={composePerson} subject={subject} setSubject={setSubject} replyHtml={replyHtml} setReplyHtml={setReplyHtml} reply={reply} setReply={setReply} attachments={attachments} onRemoveAttachment={(index) => setAttachments((current) => current.filter((_item, itemIndex) => itemIndex !== index))} onChooseFiles={chooseFiles} fileInputRef={attachmentInputRef} sending={sending} signature={signature} includeSignature={includeSignature} setIncludeSignature={setIncludeSignature} onSubmit={submitNewEmail} /> : <div className="mailbox-empty-thread"><Inbox className="h-6 w-6" /><div className="font-medium">Recipient unavailable</div><p>Open a person record with a valid email address to start a message.</p></div>) : detailLoading ? <div className="mailbox-detail-loading"><Spinner label="Loading thread…" /></div> : <ThreadView thread={thread} replyHtml={replyHtml} setReplyHtml={setReplyHtml} reply={reply} setReply={setReply} attachments={attachments} onRemoveAttachment={(index) => setAttachments((current) => current.filter((_item, itemIndex) => itemIndex !== index))} onChooseFiles={chooseFiles} fileInputRef={attachmentInputRef} sending={sending} signature={signature} includeSignature={includeSignature} setIncludeSignature={setIncludeSignature} onSubmit={submitReply} />}
        <SequenceContext thread={thread} onViewPerson={() => { window.location.assign(`/people/${thread.person_id}`); }} />
      </div>
    </div>
  );
}
